import type { IMetadata } from '@adloid-technologies/discover-sdk';

/**
 * Cadillac Lyriq (linkId 2369, project 59).
 *
 * Converted from the 1.0 repo's `src/metadata/data/metadata-2369.ts`
 * (`appData.visualiser.models[0]`, array-shaped) into the 2.0 object-keyed
 * `IMetadata` shape, following the conventions established for 2382/2377:
 * consolidate-or-split fingerprinting per entity, `autoIntelRPOCode` from
 * `brandItemCode`, real condition/isDefault/isStop threading on accessory
 * messages, model-level `hotspots` built from the default variant, and
 * `groupId`/`isDefault` derived from `removeAccessories` via connected
 * components (with single-select group categories normalised to the group's
 * dominant category — 1.0 sometimes mislabels one member).
 *
 * PARTIAL ASSET GAP: every per-entity image (colours, features) for the
 * 2025 Luxury and 2025 Sport trims is missing at the deployed 1.0 host
 * under any extension - 43 of 71 references probed individually - while
 * 2026 LYRIQ-V's entire set resolves cleanly (28 files). Missing image
 * fields are left as empty strings, same treatment as Yukon (2368).
 * 
 * 2025 Sport and 2026 LYRIQ-V share most configurationIds (7 colours, 9
 * features, 7 interactions) but differ in content (different swatch/still
 * filenames - one broken, one real), so they correctly split into
 * separate `-2026-lyriq-v`-suffixed entries rather than merging.
 *
 * Generated — see the conversion notes in ui-metadata.ts.
 */
export const metadata: IMetadata = {
  models: {
    Lyriq: {
      id: 'Lyriq',
      lightingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      postProcessingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      backgroundIdentifer: 'dome',
      variants: {
        '2025-luxury': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5113',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'common_interior',
              sku: '5112',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5102',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5154',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '7093',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
            {
              id: 'variants_parts',
              sku: '5141',
              projectId: '59',
              position: [0, 0, 0],
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        '2025-sport': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5113',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'common_interior',
              sku: '5112',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5102',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5119',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '7093',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        '2026-lyriq-v': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6348',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'common_interior',
              sku: '6354',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6350',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6369',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '7097',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
      },
      backgrounds: {
        // Backgrounds are model-level (one sku per tile for every trim), so the
        // switcher carries the 2025 Lyriq pair. 2026 LYRIQ-V still opens on its
        // own dome (7097) via its defaultSkus, but switching tiles on it loads
        // these 2025 domes; its own pair is 7097 / 7098.
        g0ZA968_RpUd: {
          isDefault: true,
          sku: '7093', // default (City Landscape 1)
          project: '59',
        },
        g0ZA968_RpUe: {
          isDefault: false,
          sku: '7094', // City Landscape 2
          project: '59',
        },
      },
      ambientLighting: {
        ambientColors: '#0040FF,#ff1300,#ff9f51,#aafef4,#00b6ff,#61ff87',
        ambientLightings: {
          ambientBlue: {
            isDefault: true,
            identifier: 'common_interior',
            variantIdentifiers: {
              '2025-luxury': 'common_interior',
              '2025-sport': 'common_interior',
              '2026-lyriq-v': 'common_interior',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [0, 64, 255],
            emissiveIntensity: 0.5,
          },
          ambientRed: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              '2025-luxury': 'common_interior',
              '2025-sport': 'common_interior',
              '2026-lyriq-v': 'common_interior',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [255, 19, 0],
            emissiveIntensity: 0.5,
          },
          ambientOrange: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              '2025-luxury': 'common_interior',
              '2025-sport': 'common_interior',
              '2026-lyriq-v': 'common_interior',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [255, 159, 81],
            emissiveIntensity: 0.5,
          },
          ambientCyan: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              '2025-luxury': 'common_interior',
              '2025-sport': 'common_interior',
              '2026-lyriq-v': 'common_interior',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [170, 254, 244],
            emissiveIntensity: 0.5,
          },
          ambientSkyBlue: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              '2025-luxury': 'common_interior',
              '2025-sport': 'common_interior',
              '2026-lyriq-v': 'common_interior',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [0, 182, 255],
            emissiveIntensity: 0.5,
          },
          ambientGreen: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              '2025-luxury': 'common_interior',
              '2025-sport': 'common_interior',
              '2026-lyriq-v': 'common_interior',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [97, 255, 135],
            emissiveIntensity: 0.5,
          },
        },
        relevantVariants: ['2025-luxury', '2025-sport', '2026-lyriq-v'],
      },
      colors: {
        exterior: {
          '4f9dd3vca75z': {
            sku: '5102',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '69xux2hlscm1': {
            sku: '5103',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '0pazineb3kjj': {
            sku: '5105',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          qzewltf6e2pf: {
            sku: '5110',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '10xy9i682nxc': {
            sku: '5108',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '37wp0lpjh309': {
            sku: '5111',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          wf9o2nslk4du: {
            sku: '5104',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '8nckjcklkf2a': {
            sku: '5100',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '4zd4onnmg2y8': {
            sku: '5107',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '4zodlcbujnve': {
            sku: '5140',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '03495u2095u209': {
            sku: '5109',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '2fa06p9t7v8f': {
            sku: '5102',
            project: '59',
            relevantVariants: [
              '2025-sport',
            ],
          },
          '2fa06p9t7v8f-2026-lyriq-v': {
            sku: '6350',
            project: '59',
            relevantVariants: [
              '2026-lyriq-v',
            ],
          },
          '5xzh26yrdinw': {
            sku: '5105',
            project: '59',
            relevantVariants: [
              '2025-sport',
            ],
          },
          '5xzh26yrdinw-2026-lyriq-v': {
            sku: '6349',
            project: '59',
            relevantVariants: [
              '2026-lyriq-v',
            ],
          },
          '2fbmj48vpgsi': {
            sku: '5110',
            project: '59',
            relevantVariants: [
              '2025-sport',
            ],
          },
          '2fbmj48vpgsi-2026-lyriq-v': {
            sku: '6353',
            project: '59',
            relevantVariants: [
              '2026-lyriq-v',
            ],
          },
          '37xytt0490j8': {
            sku: '5111',
            project: '59',
            relevantVariants: [
              '2025-sport',
            ],
          },
          '37xytt0490j8-2026-lyriq-v': {
            sku: '6357',
            project: '59',
            relevantVariants: [
              '2026-lyriq-v',
            ],
          },
          '536y2x49kxgq': {
            sku: '5104',
            project: '59',
            relevantVariants: [
              '2025-sport',
            ],
          },
          '536y2x49kxgq-2026-lyriq-v': {
            sku: '6351',
            project: '59',
            relevantVariants: [
              '2026-lyriq-v',
            ],
          },
          '4zd4onnmg2y5': {
            sku: '5107',
            project: '59',
            relevantVariants: [
              '2025-sport',
            ],
          },
          '4zd4onnmg2y5-2026-lyriq-v': {
            sku: '6355',
            project: '59',
            relevantVariants: [
              '2026-lyriq-v',
            ],
          },
          '2phvl5sq89cx': {
            sku: '5140',
            project: '59',
            relevantVariants: [
              '2025-sport',
            ],
          },
          '2phvl5sq89cx-2026-lyriq-v': {
            sku: '6356',
            project: '59',
            relevantVariants: [
              '2026-lyriq-v',
            ],
          },
        },
        interior: {
          '1c0z0wtq7ikc': {
            sku: '5153',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          '0rl5mcxrsw1t': {
            sku: '5115',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          hln6y3y86ug4: {
            sku: '5117',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          yh3xn6m12i5m: {
            sku: '5154',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          '48za413x82620': {
            sku: '5116',
            project: '59',
            relevantVariants: [
              '2025-sport',
            ],
            notCompatibleExteriorColors: [],
          },
          '48za413x82620-2026-lyriq-v': {
            sku: '6368',
            project: '59',
            relevantVariants: [
              '2026-lyriq-v',
            ],
            notCompatibleExteriorColors: [],
          },
          '48za413x82621': {
            sku: '5115',
            project: '59',
            relevantVariants: [
              '2025-sport',
            ],
            notCompatibleExteriorColors: [],
          },
          '48za413x82621-2026-lyriq-v': {
            sku: '6369',
            project: '59',
            relevantVariants: [
              '2026-lyriq-v',
            ],
            notCompatibleExteriorColors: [],
          },
          '48za413x82622': {
            sku: '5117',
            project: '59',
            relevantVariants: [
              '2025-sport',
            ],
            notCompatibleExteriorColors: [],
          },
          '48za413x82623': {
            sku: '5119',
            project: '59',
            relevantVariants: [
              '2025-sport',
            ],
            notCompatibleExteriorColors: [],
          },
        },
      },
      features: {
        '49vozj460qwe': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Roof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'Shader_Roof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '0gvs8s0ln4fh': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'Shader_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        nb8tkmndj3vg: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Shader_Side_Trim',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Side Trim',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        m37v8o8yl5x2: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Shader_Wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        eo26k4ynyg3r: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Shader_Handles',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'DOORS_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Handles',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            f6mckp6y4528: {
              // The handle glow is baked at the closed-door position, so
              // an open door leaves it hanging in mid-air. 1.0 closes the
              // doors first via `applyInteractions: { id: 'door', state:
              // 'remove', snapToLastFrame: true }`; this is its 2.0 form.
              add: false,
              snapToLastFrame: true,
            },
          },
        },
        xogfbqwjubb0: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Shader_Ac_Control',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'AC Control',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        s4f98dj7g3mk: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Rear Heated Seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        epg4m463go0z: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Info Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'Shader_Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        tvh5xpuhzsbz: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Ambient Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ngzrs5lwkgop: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Front Seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '2mn467pj8vy5': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Heated Front Seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        op4pfqvxb43s: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Wireless Charger',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'Shader_Wireless Charger',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '29kx4ja7eg7r': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2025-sport',
          ],
          messages: [
            {
              message: 'Roof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'Shader_Roof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '29kx4ja7eg7r-2026-lyriq-v': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-lyriq-v',
          ],
          messages: [
            {
              message: 'Roof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'Shader_Roof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '3ud49gqncb2e': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2025-sport',
          ],
          messages: [
            {
              message: 'Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'Shader_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '3ud49gqncb2e-2026-lyriq-v': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-lyriq-v',
          ],
          messages: [
            {
              message: 'Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'Shader_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        aayf3bzuw1dj: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2025-sport',
          ],
          messages: [
            {
              message: 'Shader_Wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'aayf3bzuw1dj-2026-lyriq-v': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-lyriq-v',
          ],
          messages: [
            {
              message: 'Shader_Wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '2g2uqgatmdw8': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2025-sport',
          ],
          messages: [
            {
              message: 'Shader_Handles',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'DOORS_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Handles',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            f9s7b4hdeiz7: {
              // The handle glow is baked at the closed-door position, so
              // an open door leaves it hanging in mid-air. 1.0 closes the
              // doors first via `applyInteractions: { id: 'door', state:
              // 'remove', snapToLastFrame: true }`; this is its 2.0 form.
              add: false,
              snapToLastFrame: true,
            },
          },
        },
        '2g2uqgatmdw8-2026-lyriq-v': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-lyriq-v',
          ],
          messages: [
            {
              message: 'Shader_Handles',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'DOORS_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Handles',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            f9s7b4hdeiz7: {
              // The handle glow is baked at the closed-door position, so
              // an open door leaves it hanging in mid-air. 1.0 closes the
              // doors first via `applyInteractions: { id: 'door', state:
              // 'remove', snapToLastFrame: true }`; this is its 2.0 form.
              add: false,
              snapToLastFrame: true,
            },
          },
        },
        '2ystj8x60810': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-sport',
          ],
          messages: [
            {
              message: 'Shader_Ac_Control',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'AC Control',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '2ystj8x60810-2026-lyriq-v': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-lyriq-v',
          ],
          messages: [
            {
              message: 'Shader_Ac_Control',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'AC Control',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        or7mlsx2z2tp: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-sport',
          ],
          messages: [
            {
              message: 'Rear Heated Seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'or7mlsx2z2tp-2026-lyriq-v': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-lyriq-v',
          ],
          messages: [
            {
              message: 'Rear Heated Seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        tppihp9c8m7o: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-sport',
          ],
          messages: [
            {
              message: 'Info Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'Shader_Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'tppihp9c8m7o-2026-lyriq-v': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-lyriq-v',
          ],
          messages: [
            {
              message: 'Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'Shader_Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        gaumznh1p962: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-sport',
          ],
          messages: [
            {
              message: 'Ambient Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'gaumznh1p962-2026-lyriq-v': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-lyriq-v',
          ],
          messages: [
            {
              message: 'Ambient Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '1jhh1m7485kl': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-sport',
          ],
          messages: [
            {
              message: 'Wireless Charger',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'Shader_Wireless Charger',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '1jhh1m7485kl-2026-lyriq-v': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-lyriq-v',
          ],
          messages: [
            {
              message: 'Wireless Charger',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'Shader_Wireless Charger',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '2g2uffqgatmdw8': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-lyriq-v',
          ],
          messages: [
            {
              message: 'Shader_Side_Trim',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Side Trim',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '2g09ffqgatmdw8': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-lyriq-v',
          ],
          messages: [
            {
              message: 'Suspension',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '2g0mjiopqgatmdw8': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-lyriq-v',
          ],
          messages: [
            {
              message: 'Brakes',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '1jhh1mvyo7485kl': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-lyriq-v',
          ],
          messages: [
            {
              message: 'Velocity Max',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '1jhh1mvfffyo7485kl': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-lyriq-v',
          ],
          messages: [
            {
              message: 'Front Seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
      },
      accessories: {
        '3ckvx6gvve78': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Sport_Variant_Parts',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2025-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '3ckvx6gvve78-2026-lyriq-v': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Vseries_Variant_Parts',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-lyriq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '3ckvcvcx6gvve78': {
          view: 'exterior',
          // Default and optional caliper share a group so selecting one turns the
          // other off (and removing the optional brings the default back).
          groupId: '2026-lyriq-v-caliper',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Vseries_Caliper_Default',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-lyriq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '3ckkvvcvcx6gvve78': {
          view: 'exterior',
          // Default and optional caliper share a group so selecting one turns the
          // other off (and removing the optional brings the default back).
          groupId: '2026-lyriq-v-caliper',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Vseries_Caliper_Optional',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Caliper',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-lyriq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
      },
      packages: {},
      interactions: {
        f6mckp6y4528: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-luxury',
          ],
        },
        tbur04pwvnag: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-luxury',
          ],
        },
        iprifpuhmp7l: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-luxury',
          ],
        },
        vbjs341ccuuu: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-luxury',
          ],
        },
        gvx72nh2lk38: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 3.9547,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-luxury',
          ],
        },
        haleh6q9o6o2: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_SHADE_OPEN',
              identifiers: [
                'common_interior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_SHADE_CLOSE',
              identifiers: [
                'common_interior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7007,
                beta: 0.232,
                radius: 4,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.704,
                beta: 2.489,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.73,
                beta: 2.3,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 4.7,
                beta: 2.01,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            '2025-luxury',
          ],
        },
        f9s7b4hdeiz7: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-sport',
            '2026-lyriq-v',
          ],
        },
        '1nad76ictp6j': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-sport',
            '2026-lyriq-v',
          ],
        },
        '3cqy8q29b96c': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-sport',
            '2026-lyriq-v',
          ],
        },
        '2ca59s6yd3u9': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-sport',
            '2026-lyriq-v',
          ],
        },
        '1dzdmvhl8vp3': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 3.9547,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-sport',
            '2026-lyriq-v',
          ],
        },
        mAlX4SRB_per: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_SHADE_OPEN',
              identifiers: [
                'common_interior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_SHADE_CLOSE',
              identifiers: [
                'common_interior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7007,
                beta: 0.232,
                radius: 4,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.704,
                beta: 2.489,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.73,
                beta: 2.3,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 4.7,
                beta: 2.01,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            '2025-sport',
          ],
        },
        'mAlX4SRB_per-2026-lyriq-v': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_SHADE_OPEN',
              identifiers: [
                'common_interior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_SHADE_CLOSE',
              identifiers: [
                'common_interior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7007,
                beta: 0.232,
                radius: 4,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.704,
                beta: 2.489,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.73,
                beta: 2.3,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 4.7,
                beta: 2.01,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            '2026-lyriq-v',
          ],
        },
      },
      hotspots: {
        exteriorCamera: {
          type: 'exterior',
          messages: {
            fov: 0.8,
            alpha: 2.02,
            beta: 1.47,
            radius: 5.7,
            lowerAlphaLimit: null,
            upperAlphaLimit: null,
            lowerBetaLimit: 0.1,
            upperBetaLimit: 1.6,
            lowerRadiusLimit: 3.2,
            upperRadiusLimit: 6.7,
            target: {
              x: 0,
              y: 0.65,
              z: 0,
            },
            isFovZoomModeOn: false,
          },
        },
        interiorCamera: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_1: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_2',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_2: {
          type: 'interior',
          messages: {},
        },
      },
      usdzPathWithVariable: '',
    },
  },
};
